/**
 * portal-invitation.ts - the portal invitation that rides in the Brokermandat's first contact.
 *
 * The broker's mandate mail carries the customer's personal activation link (Stefan 17.2,
 * Baustein P-05.S8P) instead of a separate platform invite mail. This module holds the shared
 * contract of that invitation: the token provenance, the degrade hints, the customer's
 * Portalstatus read model for the header chip, the validity band and the pure block renderer.
 *
 * Pure (no IO). The date shown in the block is the Zurich calendar day of the token's expiry.
 */

import { formatZurichDateDe } from './zurich-day';

// ── token provenance (portal_activation_token.source) ────────────────────────────

/** Who minted an activation token. Mirrors the CHECK of portal_activation_token.source. */
export const PORTAL_INVITATION_SOURCE = {
  PLATFORM_INVITE: 'platform_invite',
  PLATFORM_REMINDER: 'platform_reminder',
  BROKER_REINVITE: 'broker_reinvite',
  GRANT_INVITE: 'grant_invite',
  MANDATE_DISPATCH: 'mandate_dispatch',
  MANDATE_REMINDER: 'mandate_reminder',
  MANDATE_EINGANGSBESTAETIGUNG: 'mandate_eingangsbestaetigung',
  /** The customer asked for a fresh link on the superseded-link page. */
  PORTAL_REMINDER: 'portal_reminder',
} as const;
export type PortalInvitationSource = (typeof PORTAL_INVITATION_SOURCE)[keyof typeof PORTAL_INVITATION_SOURCE];
export const PORTAL_INVITATION_SOURCE_VALUES = Object.values(PORTAL_INVITATION_SOURCE) as readonly PortalInvitationSource[];

// ── degrade hints (the invitation never fails the mandate send) ──────────────────

/**
 * Why a mandate went out without an invitation link. Every refusal of the manual invite becomes
 * a hint here; `account_exists` is not a refusal (the customer logs in with the existing account).
 */
export const PORTAL_INVITATION_HINT = {
  PORTAL_NOT_ENABLED: 'portal_not_enabled',
  PORTAL_GROUPS_REQUIRED: 'portal_groups_required',
  PORTAL_GROUPS_SELECTION_REQUIRED: 'portal_groups_selection_required',
  NO_EMAIL: 'no_email',
  ACCOUNT_EXISTS: 'account_exists',
} as const;
export type PortalInvitationHint = (typeof PORTAL_INVITATION_HINT)[keyof typeof PORTAL_INVITATION_HINT];

/** The hints that mean "no invitation possible" (an activity tells the broker). */
export const PORTAL_INVITATION_REFUSAL_HINTS: readonly PortalInvitationHint[] = [
  PORTAL_INVITATION_HINT.PORTAL_NOT_ENABLED,
  PORTAL_INVITATION_HINT.PORTAL_GROUPS_REQUIRED,
  PORTAL_INVITATION_HINT.PORTAL_GROUPS_SELECTION_REQUIRED,
  PORTAL_INVITATION_HINT.NO_EMAIL,
];

/** Why a mandate went out without the link, in words (timeline, wizard review). */
export const PORTAL_INVITATION_HINT_TEXT: Record<PortalInvitationHint, string> = {
  portal_not_enabled: 'Kundenportal nicht aktiviert',
  portal_groups_required: 'im gruppenbasierten Modus fehlt eine Berechtigungsgruppe',
  portal_groups_selection_required: 'mehrere Berechtigungsgruppen, bitte über die Mandantenseite einladen',
  no_email: 'keine E-Mail-Adresse hinterlegt',
  account_exists: 'Zugang bereits vorhanden',
};

/** The activity line the customer's timeline carries when a mandate went out without the link. */
export function portalInvitationRefusalActivity(hint: PortalInvitationHint): string {
  return `Portal-Einladung nicht möglich: ${PORTAL_INVITATION_HINT_TEXT[hint]}`;
}

// ── validity of an activation link (portal_config.activation_link_days) ──────────

export const ACTIVATION_LINK_DAYS_DEFAULT = 30;
export const ACTIVATION_LINK_DAYS_MIN = 7;
export const ACTIVATION_LINK_DAYS_MAX = 60;

/** The 400 code of a validity outside the band. */
export const PORTAL_LINK_DAYS_OUT_OF_RANGE = 'portal_link_days_out_of_range' as const;

// ── the customer's Portalstatus (header chip) ───────────────────────────────────

export const PORTAL_STATUS_STATE = {
  KEIN_ZUGANG: 'kein_zugang',
  EINGELADEN: 'eingeladen',
  LINK_ABGELAUFEN: 'link_abgelaufen',
  AKTIV: 'aktiv',
  PORTAL_DEAKTIVIERT: 'portal_deaktiviert',
  /** P6-S7: the customer's own access runs out (inaktiv): lesend until readOnlyUntil, then deaktiviert. */
  ZUGANG_LESEND: 'zugang_lesend',
  ZUGANG_DEAKTIVIERT: 'zugang_deaktiviert',
} as const;
export type PortalStatusState = (typeof PORTAL_STATUS_STATE)[keyof typeof PORTAL_STATUS_STATE];

/** One label per state; `eingeladen` with an existing account reads PORTAL_STATUS_LABEL_ACCOUNT_EXISTS. */
export const PORTAL_STATUS_LABEL: Record<PortalStatusState, string> = {
  kein_zugang: 'Kein Zugang',
  eingeladen: 'Eingeladen',
  link_abgelaufen: 'Link abgelaufen',
  aktiv: 'Kundenportal aktiv',
  portal_deaktiviert: 'Kundenportal nicht aktiviert',
  zugang_lesend: 'Portal lesend',
  zugang_deaktiviert: 'Portalzugang deaktiviert',
};
export const PORTAL_STATUS_LABEL_ACCOUNT_EXISTS = 'Eingeladen (Konto vorhanden)';

/** GET /customers/:subjectType/:subjectId/portal-status. */
export interface PortalStatusDto {
  state: PortalStatusState;
  /** The invited address already has an activated portal account (another broker's portal, or never logged in here). */
  accountExists: boolean;
  label: string;
  invitedAt: string | null;
  /** Expiry of the customer's live link; null when none is live. */
  linkValidUntil: string | null;
  activatedAt: string | null;
  lastTokenSource: PortalInvitationSource | null;
  /** P6-S7: zugang_lesend only, the instant the access runs out (ISO, access_expires_at); the chip reads «Portal lesend bis <the Zurich day before>» (portalReadOnlyUntilLabel). */
  readOnlyUntil?: string | null;
}

/** The label of a status read (one place, api and web). */
export function portalStatusLabel(state: PortalStatusState, accountExists: boolean): string {
  if (state === PORTAL_STATUS_STATE.EINGELADEN && accountExists) return PORTAL_STATUS_LABEL_ACCOUNT_EXISTS;
  return PORTAL_STATUS_LABEL[state];
}

// ── the block in the mail (P-05.S8P) ────────────────────────────────────────────

/** Where the block goes in the broker's covering text (the stored, machine form). */
export const PORTAL_INVITATION_PLACEHOLDER = '{portal_einladung}';

/**
 * The readable marker the editor shows in place of the placeholder. The send treats it exactly like
 * the placeholder: the line is replaced by the block. The broker may move or delete the line.
 */
export const PORTAL_INVITATION_MARKER = '[Portal-Einladung wird beim Versand eingefügt]';

/** What the stored copy of a sent mail or Brief shows in place of the token (never the plain link). */
export const PORTAL_TOKEN_MASK = '[entfernt]';

export type PortalInvitationVariant = 'invite' | 'active' | 'existing_account' | 'none';

/** What the mandate path fills into the covering text. Holds the plain link: never logged. */
export interface PortalInvitationBlock {
  variant: PortalInvitationVariant;
  /** The activation link (variant invite) or the plain portal link (active, existing_account). */
  link: string | null;
  /** The token's expiry instant (ISO), variant invite only. */
  validUntil: string | null;
  /** Printed as the activation code on a Brief (post channel); null in a mail. */
  code: string | null;
}

export interface BuildPortalInvitationBlockInput {
  variant: PortalInvitationVariant;
  link: string | null;
  /** ISO instant; rendered as the Zurich calendar day dd.MM.yyyy. */
  validUntil: string | null;
  anrede: 'sie' | 'du';
  /** KMU + Du uses euch/eure (17.2). Defaults to PRIV. */
  segment?: 'KMU' | 'PRIV';
  /** The activation code printed on a Brief (post channel). */
  code?: string | null;
}

/**
 * The P-05.S8P block, Stefan 17.2 wording, Sie/Du (KMU: euch/eure). `invite` carries the link and
 * its validity, `active` and `existing_account` the plain portal link without a token, `none`
 * renders nothing. No `[...]` placeholder of the concept ever remains in the output.
 */
export function buildPortalInvitationBlock(input: BuildPortalInvitationBlockInput): string {
  if (input.variant === 'none') return '';
  const du = input.anrede === 'du';
  const kmuDu = du && input.segment === 'KMU';
  const link = input.link ?? '';

  if (input.variant === 'active') {
    return !du
      ? `Ihr Kundenportal ist aktiv: Sie können das Brokermandat dort digital unterzeichnen (${link}).`
      : kmuDu
        ? `Euer Kundenportal ist aktiv: Ihr könnt das Brokermandat dort digital unterzeichnen (${link}).`
        : `Dein Kundenportal ist aktiv: Du kannst das Brokermandat dort digital unterzeichnen (${link}).`;
  }

  if (input.variant === 'existing_account') {
    return !du
      ? `Sie haben bereits einen Zugang zum Kundenportal: Melden Sie sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an (${link}).`
      : kmuDu
        ? `Ihr habt bereits einen Zugang zum Kundenportal: Meldet euch mit eurer E-Mail-Adresse und eurem Passwort an (${link}).`
        : `Du hast bereits einen Zugang zum Kundenportal: Melde dich mit deiner E-Mail-Adresse und deinem Passwort an (${link}).`;
  }

  const intro = !du
    ? 'Ihr persönliches Kundenportal: Hier sehen Sie Ihre Policen, Dokumente und Nachrichten jederzeit an einem Ort und können das Brokermandat digital unterzeichnen.'
    : kmuDu
      ? 'Euer persönliches Kundenportal: Hier seht ihr eure Policen, Dokumente und Nachrichten jederzeit an einem Ort und könnt das Brokermandat digital unterzeichnen.'
      : 'Dein persönliches Kundenportal: Hier siehst du deine Policen, Dokumente und Nachrichten jederzeit an einem Ort und kannst das Brokermandat digital unterzeichnen.';
  const date = input.validUntil !== null ? formatZurichDateDe(input.validUntil) : null;
  const validity =
    date !== null
      ? `Persönlicher Link, gültig bis ${date}, Anmeldung mit Zwei-Faktor.`
      : 'Persönlicher Link, Anmeldung mit Zwei-Faktor.';
  const lines = [intro, `Kundenportal jetzt aktivieren: ${link}`, validity];
  if (input.code) lines.push(`Aktivierungscode: ${input.code}`);
  return lines.join('\n');
}

// ── the invitation plan in the wizard review ────────────────────────────────────

/** What the mandate's first contact will do for the portal (read only; nothing is minted here). */
export type PortalInvitationPlanned = 'invite' | 'renew' | 'active' | 'existing_account' | 'none';

export interface PortalInvitationPlanDto {
  planned: PortalInvitationPlanned;
  /** The validity in days a minted link gets (portal_config.activation_link_days). */
  linkDays: number;
  /** Signers who get their own grant invitation (count only). */
  grantInvites: number;
  hint: PortalInvitationHint | null;
  /**
   * The block as it will be inserted at the send, with the personal link shown masked: a read-only
   * preview under the editor (the block is never part of the editable text). null when none.
   */
  previewText?: string | null;
}

// ── the activation page ─────────────────────────────────────────────────────────

/** The 401 codes of POST /portal/auth/activate. */
export const PORTAL_ACTIVATE_ERROR = {
  INVALID_TOKEN: 'invalid_token',
  /** The link was replaced by a newer one (each customer communication re-mints it). */
  TOKEN_SUPERSEDED: 'token_superseded',
} as const;

/** POST /portal/auth/activation-link/renew answers 202 with this body in every case (no enumeration). */
export const PORTAL_RENEW_LINK_STATUS = { ACCEPTED: 'accepted' } as const;
